import React from "react";
import Image from "../image/Image";
import LinkWrapper from "../linkWrapper/LinkWrapper";
import { getImageLink } from "../../../utils/images";
import { decodeText } from "../../../utils/richText";

// Layout/alignment overrides are inline styles on purpose: the flone theme pins
// .single-slider to align-items: flex-end plus a large slider-content top padding (that's
// the stock "text sits low" look), and utility classes would lose that specificity fight.
// The defaults (split / left / bottom) apply NO overrides, so existing slides render
// exactly as before.
const VERTICAL_ALIGN = { top: "flex-start", middle: "center" };
const TEXT_ALIGN = { center: "center", right: "end" };

const SlideContent = ({ badge, eyebrow, title, link, linkNode, contentStyle, light }) => (
    <div className="slider-content slider-animated-1" style={contentStyle}>
        {badge && <span className="badge bg-purple-2 text-dark text-uppercase mb-3 animated d-inline-block">{decodeText(badge)}</span>}
        {eyebrow && (
            <h3 className="animated" style={light ? { color: "#fff", marginBottom: "0.75rem" } : undefined}>
                {decodeText(eyebrow)}
            </h3>
        )}
        {title && (
            <h1 className="animated" style={light ? { color: "#fff", marginBottom: "1.5rem" } : undefined}>
                {decodeText(title)}
            </h1>
        )}
        {link.label && (
            <div className="slider-btn btn-hover">
                {/* The theme's .slider-btn a is near-black with a dark border — inline
                    white on the full-background layout, or the CTA vanishes on the scrim. */}
                <LinkWrapper linkNode={linkNode} linkClass="animated" linkStyle={light ? { color: "#fff", borderColor: "#fff" } : undefined}>
                    {decodeText(link.label)}
                </LinkWrapper>
            </div>
        )}
    </div>
);

const HeroSlide = (props) => {
    const { link = {}, layout = "split", textAlign = "left", verticalAlign = "bottom" } = props;

    // A picked vertical alignment also neutralizes the theme's slider-content top padding —
    // with it, "top" and "middle" would still render near the bottom. The full-background
    // layout drops it always: inside the glass panel the padding is just dead space.
    const verticalOverride = VERTICAL_ALIGN[verticalAlign];
    const contentStyle = verticalOverride || layout === "full" ? { paddingTop: 0 } : undefined;
    const textAlignClass = TEXT_ALIGN[textAlign] ? `text-${TEXT_ALIGN[textAlign]} justify-content-${TEXT_ALIGN[textAlign]}` : "";

    const content = (
        <SlideContent
            badge={props.badge}
            eyebrow={props.eyebrow}
            title={props.title}
            link={link}
            linkNode={props.link}
            contentStyle={contentStyle}
            light={layout === "full"}
        />
    );

    if (layout === "full") {
        const src = getImageLink(props.image);

        // Readability on arbitrary images comes from a glass panel behind the text block
        // (semi-transparent dark + backdrop blur), not from darkening the whole image —
        // the full-bleed scrim stays light so the picture remains visible.
        const panelStyle = {
            background: "rgba(17, 24, 39, 0.55)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            borderRadius: "12px",
            padding: "2rem 2.5rem",
            maxWidth: "560px",
            margin: "3rem 0",
        };

        return (
            <div
                className="single-slider slider-height-1 position-relative"
                style={{
                    backgroundImage: src ? `url(${src})` : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    alignItems: verticalOverride,
                }}
            >
                <div className="position-absolute top-0 start-0 w-100 h-100" style={{ background: "rgba(0, 0, 0, 0.2)" }} />
                <div className="container position-relative">
                    <div className={`d-flex ${textAlignClass}`}>
                        <div style={panelStyle}>{content}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="single-slider slider-height-1 bg-purple" style={{ alignItems: verticalOverride }}>
            <div className="container">
                <div className={`row ${layout === "splitReverse" ? "flex-row-reverse" : ""}`}>
                    <div className={`col-xl-6 col-lg-6 col-md-6 col-12 col-sm-6 d-flex ${textAlignClass}`}>{content}</div>

                    {props.image && (
                        <div className="col-xl-6 col-lg-6 col-md-6 col-12 col-sm-6">
                            <div className="slider-single-img slider-animated-1">
                                <Image imageNode={props.image} className="animated img-fluid" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HeroSlide;